import { createSlice } from '@reduxjs/toolkit';

import { updateCoursePosition } from '../services/supabaseClient/courseService';

const normalizeCourseUnits = (units) =>
  (Array.isArray(units) ? units : [])
    .map((unit, index) => {
      const parsedPosition = Number(unit?.position ?? unit?.unit_index ?? index);
      return {
        ...unit,
        position: Number.isFinite(parsedPosition) ? parsedPosition : index,
      };
    })
    .sort((a, b) => {
      const byPosition = Number(a.position) - Number(b.position);
      if (byPosition !== 0) return byPosition;
      return String(a?.title || a?.unit_title || '').localeCompare(
        String(b?.title || b?.unit_title || '')
      );
    });

// ✅ Load from localStorage if available
const loadInitialState = () => {
  const storedCourses = localStorage.getItem('courses');
  const storedActiveCourseId = localStorage.getItem('activeCourseId');

  return {
    courses: storedCourses ? JSON.parse(storedCourses) : [],
    activeCourseId: storedActiveCourseId || null,
  };
};

const initialState = loadInitialState();

const coursesSlice = createSlice({
  name: 'courses',
  initialState,
  reducers: {
    // ✅ Overwrite full course list (e.g., after login)
    setCourses: (state, action) => {
      const normalizedCourses = (action.payload || []).map((c) => ({
        ...c,
        difficultyLevel: c.difficultyLevel || c.difficulty_level || 'Beginner',
        units: normalizeCourseUnits(c.units), // 🧩 Ensure stable unit order
      }));

      state.courses = normalizedCourses;
      localStorage.setItem('courses', JSON.stringify(normalizedCourses));
    },

    // ✅ Add single course to list
    addCourse: (state, action) => {
      const newCourse = {
        ...action.payload,
        difficultyLevel:
          action.payload.difficultyLevel || action.payload.difficulty_level || 'Beginner',
        units: normalizeCourseUnits(action.payload.units),
      };

      if (!newCourse.id) {
        // eslint-disable-next-line no-console
        console.warn('⚠️ Course ID missing. Not adding to Redux.');
        return;
      }

      const existingIndex = state.courses.findIndex((c) => c.id === newCourse.id);
      if (existingIndex >= 0) {
        const existing = state.courses[existingIndex];
        state.courses[existingIndex] = {
          ...existing,
          ...newCourse,
          // Keep existing units if incoming payload is temporarily empty.
          units:
            Array.isArray(newCourse.units) && newCourse.units.length > 0
              ? newCourse.units
              : existing.units || [],
        };
      } else {
        state.courses.push(newCourse);
      }
      localStorage.setItem('courses', JSON.stringify(state.courses));
    },

    // ✅ Update existing course (by ID)
    updateCourse: (state, action) => {
      const updated = {
        ...action.payload,
        difficultyLevel:
          action.payload.difficultyLevel || action.payload.difficulty_level || 'Beginner',
        units: normalizeCourseUnits(action.payload.units),
      };

      const idx = state.courses.findIndex((c) => c.id === updated.id);
      if (idx !== -1) {
        const existing = state.courses[idx];
        state.courses[idx] = updated;
        state.courses[idx].units =
          updated.units.length > 0 ? updated.units : normalizeCourseUnits(existing.units);
        localStorage.setItem('courses', JSON.stringify(state.courses));
        updateCoursePosition(updated.id, updated.currentLessonIndex);
      }
    },

    // 🗑️ Delete course and handle active course cleanup
    deleteCourse: (state, action) => {
      const courseId = action.payload;
      state.courses = state.courses.filter((course) => course.id !== courseId);

      if (state.activeCourseId === courseId) {
        state.activeCourseId = null;
        localStorage.removeItem('activeCourseId');
      }

      localStorage.setItem('courses', JSON.stringify(state.courses));
    },

    // ⭐ Set the currently active course
    setActiveCourse: (state, action) => {
      state.activeCourseId = action.payload;
      localStorage.setItem('activeCourseId', action.payload);
    },
  },
});

// ✅ Actions
export const { setCourses, addCourse, updateCourse, deleteCourse, setActiveCourse } =
  coursesSlice.actions;

// ✅ Selectors
export const selectCourses = (state) => state.courses.courses;
export const selectActiveCourseId = (state) => state.courses.activeCourseId;
export const selectActiveCourse = (state) =>
  state.courses.courses.find((c) => c.id === state.courses.activeCourseId) || null;

export default coursesSlice.reducer;
